import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  useGetProductInstructionVideosQuery,
} from '../../redux-modules/service';
import { VideoIframe } from '../../components/ui/view/VideoIframe';
import { TagFilterList } from '../../components/ui/view/TagFilterList';
import { countTags } from '../../util/TagFilter';
import { ShowMoreLink } from '../../components/ui/view/ShowMoreLink';
import { Picture } from '../../components/ui/Picture';
import { ShareFromSquare } from '../../components/ui/icon/ShareFromSquare';
import { Link } from '../../components/ui/icon/Link';
import { ArrowUpRightFromSquare } from '../../components/ui/icon/ArrowUpRightFromSquare';
import { convertMinsToTimestamp } from '../../util/ConvertMinsToTimestamp';
import { ArrowRight } from '../../components/ui/icon/ArrowRight';
import { ArrowLeft } from '../../components/ui/icon/ArrowLeft';

function ProductDetailInstructionVideos({
  productId,
  loadingText,
}) {
  const {
    data,
    isFetching,
  } = useGetProductInstructionVideosQuery({ productId });
  const [limit, setLimit] = useState(3);
  const [showAll, setShowAll] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState(null);

  if (isFetching) {
    return (<div className="container">{loadingText}</div>);
  }

  const { total, translates } = data;
  const { product } = translates;
  let { instructionVideos } = data;

  const categories = countTags(instructionVideos, (item) => item.categories);

  if (categoryFilter) {
    instructionVideos = instructionVideos.filter(
      (instructionVideo) => instructionVideo.categories.indexOf(categoryFilter) !== -1,
    );
  }

  instructionVideos = instructionVideos.slice(0, limit);

  return (
    <div className="section-main">
      <div className="container">
        <div className="section-main-head">
          <div className="row align-item-center">
            <div className="col-12 col-lg">
              <h2>{Craft.t(product, 'Instruction videos')}</h2>
            </div>
          </div>
        </div>

        <TagFilterList
          tags={categories}
          totalCount={total}
          selectedTag={categoryFilter}
          onTagSelected={(tag) => setCategoryFilter(tag)}
        />

        {instructionVideos.map((instructionVideo) => (
          instructionVideo.videos.map((video, index) => (
            <div key={video.id} className="popup-holder">
              <div className={`main-modal rounded ${instructionVideo.videos.length > 1 ? 'large' : ''}`} id={`modal-instruction-videos-${video.id}`}>
                <div className="main-modal-close-head" />

                <div className="row g-4">
                  <div className={instructionVideo.videos.length > 1 ? 'col-lg-8' : 'col-12'}>
                    <div className="video-wrap ratio ratio-16x9">
                      {video.modal.thumbnailUrl ? (
                        <img src={video.modal.thumbnailUrl} alt={video.title} loading="lazy" />
                      ) : (
                        <div className="bg-wrap">
                          <Picture image={video.modal} alt={video.title} defaultTransform="lg-min" loading="lazy" />
                        </div>
                      )}

                      {video.videoIframeSrc && (
                        <VideoIframe src={video.videoIframeSrc} />
                      )}
                    </div>
                    {instructionVideo.videos.length > 1 && (
                      <div className="d-flex mb-3">
                        {instructionVideo.videos[index - 1] && (
                          <a
                            href={`#modal-instruction-videos-${instructionVideo.videos[index - 1].id}`}
                            className="main-modal-opener icon-link"
                          >
                            <ArrowLeft width="18" height="13" />
                            {Craft.t(product, 'Previous chapter')}
                          </a>
                        )}
                        {instructionVideo.videos[index + 1] && (
                          <a
                            href={`#modal-instruction-videos-${instructionVideo.videos[index + 1].id}`}
                            className="main-modal-opener icon-link ms-auto"
                          >
                            {Craft.t(product, 'Next chapter')}
                            <ArrowRight width="18" height="13" />
                          </a>
                        )}
                      </div>
                    )}
                    <ul className="list-meta">
                      {video.categories.map((category) => (
                        <span key={category}>{category}</span>
                      ))}
                    </ul>
                    <h3>{video.title}</h3>
                    {/* eslint-disable-next-line react/no-danger */}
                    <div className="text-black" dangerouslySetInnerHTML={{ __html: video.description }} />
                    <div className="btn-wrap">
                      {video.link && (
                        <a className="btn btn-secondary" href={video.link.url} target="_blank" rel="noreferrer">
                          <ArrowUpRightFromSquare />
                          {video.link.label}
                        </a>
                      )}
                      {(video.videoIframeSrc || video.link) && (
                        <div className="position-relative">
                          <button
                            className="btn-share dropdown-toggle icon-link"
                            type="button"
                            data-bs-toggle="dropdown"
                            data-bs-auto-close="outside"
                            aria-expanded="false"
                          >
                            <ShareFromSquare />
                            <span>
                              &nbsp;
                              {Craft.t(product, 'Share')}
                            </span>
                          </button>
                          <div className="drop-share dropdown-menu">
                            <ul className="list-share">
                              {video.videoIframeSrc && (
                                <li>
                                  <button type="button" data-clipboard-text={video.videoShareUrl}>
                                    <Link />
                                    <span>{Craft.t(product, 'Copy "{provider}" link', { provider: video.videoProvider })}</span>
                                  </button>
                                </li>
                              )}
                              {video.link && (
                                <li>
                                  <button type="button" data-clipboard-text={video.link.url}>
                                    <Link />
                                    <span>{Craft.t(product, 'Copy "{provider}" link', { provider: video.link.label })}</span>
                                  </button>
                                </li>
                              )}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {instructionVideo.videos.length > 1 && (
                    <div className="col-lg-4">
                      <h3>{Craft.t(product, 'Chapters')}</h3>
                      <ol className="list-unstyled">
                        {instructionVideo.videos.map((videoLink) => (
                          <li key={`video${videoLink.id}`}>
                            <a href={`#modal-instruction-videos-${videoLink.id}`} className={`main-modal-opener d-flex ${videoLink.id === video.id ? 'fw-bold' : ''}`}>
                              {videoLink.duration && (
                                <span className="badge rounded-pill text-bg-dark h-100 mt-1 me-2">
                                  <span>{convertMinsToTimestamp(videoLink.duration)}</span>
                                </span>
                              )}
                              {videoLink.title}
                            </a>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        ))}

        <div className="row spacer-lg">
          {instructionVideos.map((instructionVideo) => (
            <div key={instructionVideo.id} className="col-12 col-md-6 col-lg-4 mb-4 mb-lg-4b">
              <a href={`#modal-instruction-videos-${instructionVideo.id}`} className="card-feature main-modal-opener">
                <div className="card-visual">
                  {instructionVideo.thumbnail.thumbnailUrl ? (
                    <picture>
                      <img src={instructionVideo.thumbnail.thumbnailUrl} alt={instructionVideo.title} loading="lazy" />
                    </picture>
                  ) : (
                    <Picture image={instructionVideo.thumbnail} alt={instructionVideo.title} defaultTransform="xl-min" loading="lazy" />
                  )}
                  {(instructionVideo.duration || instructionVideo.videos.length > 1) && (
                    <span className="mark-video-wrap">
                      {instructionVideo.duration && (
                        <span className="mark-video">{convertMinsToTimestamp(instructionVideo.duration)}</span>
                      )}
                      {instructionVideo.videos.length > 1 && (
                        <span className="mark-video mark-img ms-1">
                          {Craft.t(product, '{count} chapters', { count: instructionVideo.videos.length })}
                        </span>
                      )}
                    </span>
                  )}
                </div>
                <div className="card-body">
                  <h3>{instructionVideo.title}</h3>
                  <ul className="list-meta">
                    {instructionVideo.categories.map((category) => (
                      <li key={category}>{category}</li>
                    ))}
                  </ul>
                </div>
              </a>
            </div>
          ))}
        </div>
        {total > 3 && (
          <ShowMoreLink
            label={
              showAll
                ? Craft.t(product, 'Hide instruction videos')
                : Craft.t(product, 'Show all instruction videos')
            }
            onClick={() => {
              if (showAll) {
                setLimit(3);
                setShowAll(false);
              } else {
                setLimit(total);
                setShowAll(true);
              }
            }}
            chevronUp={showAll}
          />
        )}
      </div>
    </div>
  );
}

ProductDetailInstructionVideos.propTypes = {
  productId: PropTypes.string.isRequired,
  loadingText: PropTypes.string.isRequired,
};

export { ProductDetailInstructionVideos };
